/**
 * Esperar a que exista lo que el paso quiere resaltar.
 *
 * No se puede dar por hecho que el objetivo está en el DOM cuando el paso
 * arranca: las páginas son `lazyPage()` + `<SuspensePage>` —el chunk se
 * descarga— y encima pintan shimmer mientras cargan sus datos
 * (`docs/SHIMMER_SYSTEM.md`). Entre que se navega y que aparece el botón
 * «Nueva sucursal» pasan dos renders y una petición.
 *
 * De ahí `MutationObserver` en vez de un `setTimeout` a ojo: se resuelve en
 * cuanto el nodo entra, y sólo se espera lo que haga falta.
 *
 * **Nunca lanza.** Si el objetivo no llega, devuelve `null` y el motor
 * decide: degradar el paso a centrado o saltarlo. Una guía que se cuelga
 * esperando un botón es peor que no tener guía.
 */

const DEFAULT_TIMEOUT_MS = 8000;

interface WaitOptions {
  timeoutMs?: number;
  /** Para cancelar cuando el usuario cierra la guía o cambia de paso. */
  signal?: AbortSignal;
}

/** El selector del ancla. Las anclas son `data-tour="nivel.pantalla.elemento"`. */
export function tourSelector(target: string): string {
  return `[data-tour="${CSS.escape(target)}"]`;
}

/**
 * Espera a que el selector case con un elemento **visible**, y se queda con
 * el primero que lo esté.
 *
 * Lo de recorrer todas las coincidencias no es celo: la barra lateral se
 * pinta dos veces —la de escritorio, oculta por debajo de `lg`, y la del
 * cajón móvil—, así que un ancla del menú casa con dos nodos y uno de ellos
 * mide cero. Quedarse con el primero a secas resaltaría la nada en móvil o
 * en escritorio, según el orden del DOM.
 */
export function waitForElement(
  selector: string,
  { timeoutMs = DEFAULT_TIMEOUT_MS, signal }: WaitOptions = {},
): Promise<HTMLElement | null> {
  const find = (): HTMLElement | null => {
    const candidates = document.querySelectorAll<HTMLElement>(selector);
    for (const el of candidates) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return el;
    }
    return null;
  };

  const immediate = find();
  if (immediate) return Promise.resolve(immediate);
  if (signal?.aborted) return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;

    const finish = (el: HTMLElement | null) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      resolve(el);
    };

    const onAbort = () => finish(null);

    const observer = new MutationObserver(() => {
      const el = find();
      if (el) finish(el);
    });

    const timer = setTimeout(() => finish(null), timeoutMs);

    signal?.addEventListener('abort', onAbort, { once: true });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // Sólo estos dos: un observador de todos los atributos sobre el body
      // entero se dispara con cada clase que cambia al pasar el ratón.
      attributeFilter: ['data-tour', 'style'],
    });
  });
}

/** Lo mismo, para un ancla `data-tour`. */
export function waitForTarget(
  target: string,
  options?: WaitOptions,
): Promise<HTMLElement | null> {
  return waitForElement(tourSelector(target), options);
}

/**
 * Si hay un modal abierto que no sea la propia guía.
 *
 * La tarjeta de la guía también es un `role="dialog"`, así que se excluye
 * por `data-tour-ui`. Sin esa exclusión el motor creería que siempre hay un
 * modal abierto y no navegaría nunca.
 */
export function hasOpenDialog(): boolean {
  return document.querySelector('[role="dialog"]:not([data-tour-ui])') !== null;
}

/**
 * Espera a que el usuario cierre el modal que tenga abierto.
 *
 * Navegar con un modal abierto tira lo que haya escrito dentro:
 * `useDirtyGuard` protege el cierre del propio modal, pero es local a él y
 * no ve venir una navegación del router. Así que la guía espera, y si el
 * usuario se queda ahí, sigue sin bloquearlo.
 */
export function waitForNoDialog(
  { timeoutMs = 30000, signal }: WaitOptions = {},
): Promise<boolean> {
  if (!hasOpenDialog()) return Promise.resolve(true);
  if (signal?.aborted) return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;

    const finish = (closed: boolean) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      resolve(closed);
    };

    const onAbort = () => finish(false);

    const observer = new MutationObserver(() => {
      if (!hasOpenDialog()) finish(true);
    });

    const timer = setTimeout(() => finish(false), timeoutMs);

    signal?.addEventListener('abort', onAbort, { once: true });
    observer.observe(document.body, { childList: true, subtree: true });
  });
}
